import React, { useEffect, useState } from 'react';
import { Box, Paper, Typography, Button, Chip, Alert, CircularProgress, Card, CardMedia } from '@mui/material';
import { useParams, useNavigate } from 'react-router-dom';
import { Star, PlayCircle, MicNone, CheckCircle } from '@mui/icons-material';
import api from '../utils/api';

const keyframes = `
  @keyframes pulse {
    0% { transform: scale(1); }
    50% { transform: scale(1.1); }
    100% { transform: scale(1); }
  }
  
  @keyframes bounce {
    0%, 20%, 50%, 80%, 100% { transform: translateY(0); }
    40% { transform: translateY(-10px); }
    60% { transform: translateY(-5px); }
  }
  
  @keyframes shimmer {
    0% { background-position: -200px 0; }
    100% { background-position: calc(200px + 100%) 0; }
  }
`;

if (typeof document !== 'undefined') {
  const style = document.createElement('style');
  style.textContent = keyframes;
  document.head.appendChild(style);
}

export default function StartPage({ isSequence = false }) {
  const { linkId } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [micStatus, setMicStatus] = useState('prompt'); // prompt | granted | denied
  const [checkingMic, setCheckingMic] = useState(false);
  const [test, setTest] = useState(null);
  const [sequence, setSequence] = useState(null);
  const [currentPassageIndex, setCurrentPassageIndex] = useState(0);

  useEffect(() => {
    const fetchData = async () => {
      try {
        if (isSequence) {
          const [seqRes, currentRes] = await Promise.all([
            api.get(`/api/sequences/${linkId}`),
            api.get(`/api/sequences/${linkId}/current-passage`)
          ]);
          setSequence(seqRes.data);
          setCurrentPassageIndex(currentRes.data.currentIndex || 0);
          if (currentRes.data.completed) {
            navigate(`/sequence/${linkId}`); // will render finish UI in the test page
            return;
          }
        } else {
          const res = await api.get(`/api/tests/${linkId}`);
          setTest(res.data);
        }
        setLoading(false);
      } catch (e) {
        console.error('Failed to load data for start page:', e);
        setError(isSequence ? 'Sequence not found' : 'Test not found');
        setLoading(false);
      }
    };
    fetchData();
  }, [linkId, isSequence, navigate]);

  useEffect(() => {
    navigator.permissions?.query({ name: 'microphone' }).then(status => {
      setMicStatus(status.state);
      status.onchange = () => setMicStatus(status.state);
    }).catch(() => setMicStatus('prompt'));
  }, []);

  const handleCheckMic = async () => {
    setCheckingMic(true);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      stream.getTracks().forEach(t => t.stop());
      setMicStatus('granted');
    } catch (e) {
      setMicStatus('denied');
    } finally {
      setCheckingMic(false);
    }
  };

  const handleStart = () => {
    if (micStatus !== 'granted') return;

    // Preserve URL query params (token, returnUrl) when navigating to the test
    const currentParams = new URLSearchParams(window.location.search);
    const queryString = currentParams.toString();
    const queryPart = queryString ? `?${queryString}` : '';

    if (isSequence) {
      navigate(`/sequence/${linkId}${queryPart}`, { state: { startNow: true } });
    } else {
      navigate(`/test/${linkId}${queryPart}`, { state: { startNow: true } });
    }
  };

  if (loading) return (
    <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh"><CircularProgress /></Box>
  );
  if (error) return (
    <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh"><Alert severity="error">{error}</Alert></Box>
  );

  const studentName = isSequence ? sequence?.childName : test?.childName;
  const title = isSequence ? (sequence?.passages?.[currentPassageIndex]?.passageId?.title || 'Reading Passage') : test?.passageId?.title;

  const instructions = [
    'Sit in a quiet, well‑lit place with minimal background noise.',
    'Use a working microphone and keep it close, but not touching your mouth.',
    'Speak clearly at a normal pace and volume. Do not whisper.',
    'Read each highlighted sentence. The test auto‑advances after a brief pause.',
    'If you make a mistake, you can correct yourself and continue.',
    'Do not refresh or close the browser until the test finishes.',
  ];

  const sequenceNotes = isSequence ? [
    'This is a sequence of passages. You will read them one by one.',
    'A short message will appear between passages before the next one begins.',
    'You may take a short pause before pressing Start on each passage.',
  ] : [];

  return (
    <Box sx={{
      display: 'flex', 
      flexDirection: 'column', 
      alignItems: 'center', 
      justifyContent: 'flex-start', 
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #ff9a9e 0%, #fecfef 30%, #fad0c4 70%, #ffd1ff 100%)',
      width: '100%', 
      px: { xs: 1, sm: 2 }, 
      pt: { xs: 1, sm: 2 },
      pb: { xs: 1, sm: 2 },
      position: 'relative',
      overflow: 'hidden'
    }}>
      {/* Floating background decorations */}
      <Box sx={{
        position: 'absolute',
        top: '20%',
        left: '5%',
        width: 60,
        height: 60,
        borderRadius: '50%',
        background: 'linear-gradient(135deg, #ffeb3b 0%, #ffc107 100%)',
        opacity: 0.3,
        animation: 'bounce 3s infinite',
        zIndex: 0
      }} />
      <Box sx={{
        position: 'absolute',
        top: '60%',
        right: '8%',
        width: 40,
        height: 40,
        borderRadius: '50%',
        background: 'linear-gradient(135deg, #81c784 0%, #4caf50 100%)',
        opacity: 0.4,
        animation: 'bounce 4s infinite 1s',
        zIndex: 0
      }} />
      <Box sx={{
        position: 'absolute',
        top: '80%',
        left: '15%',
        width: 30,
        height: 30,
        borderRadius: '50%',
        background: 'linear-gradient(135deg, #64b5f6 0%, #2196f3 100%)',
        opacity: 0.3,
        animation: 'bounce 5s infinite 2s',
        zIndex: 0
      }} />
      
      <Paper sx={{
        width: '100%', 
        maxWidth: { xs: '100%', sm: 900 }, 
        borderRadius: { xs: 3, sm: 4 }, 
        overflow: 'hidden', 
        boxShadow: '0 25px 80px rgba(255,154,158,0.4)',
        position: 'relative',
        zIndex: 1,
        mb: { xs: 1, sm: 1 }
      }}>
        {/* Fun decorative elements */}
        <Box sx={{ position: 'absolute', top: 20, right: 20, zIndex: 1 }}>
          <Star sx={{ color: '#ffd54f', fontSize: 24, animation: 'pulse 2s infinite' }} />
        </Box>
        <Box sx={{ position: 'absolute', top: 40, right: 60, zIndex: 1 }}>
          <Star sx={{ color: '#81c784', fontSize: 16, animation: 'pulse 2s infinite 0.5s' }} />
        </Box>
        
        <Box sx={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          p: { xs: 1.5, sm: 2.5 }, color: 'white', position: 'relative'
        }}>
          {/* Display passage image if available */}
          {((isSequence && sequence?.passages?.[currentPassageIndex]?.passageId?.image) || 
            (!isSequence && test?.passageId?.image)) && (
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'center', 
              mb: 2,
              position: 'relative'
            }}>
              <Box sx={{
                position: 'relative',
                borderRadius: 3,
                overflow: 'hidden',
                boxShadow: '0 12px 30px rgba(0,0,0,0.3)',
                border: '4px solid rgba(255,255,255,0.3)',
                maxWidth: { xs: '200px', sm: '240px' },
                width: '100%'
              }}>
                <img 
                  src={`${process.env.REACT_APP_API_URL}/passages/${
                    isSequence 
                      ? sequence?.passages?.[currentPassageIndex]?.passageId?.image 
                      : test?.passageId?.image
                  }`}
                  alt="Story illustration"
                  style={{ 
                    borderRadius: '8px',
                    width: '100%',
                    height: 'auto',
                    maxHeight: '140px',
                    objectFit: 'cover'
                  }}
                />
                <Box sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background: 'linear-gradient(45deg, rgba(255,154,158,0.1) 0%, rgba(255,206,239,0.1) 100%)',
                  pointerEvents: 'none'
                }} />
                
                {/* Floating decorative elements */}
                <Box sx={{
                  position: 'absolute',
                  top: -8,
                  right: -8,
                  background: 'linear-gradient(135deg, #ffd54f 0%, #ffab40 100%)',
                  borderRadius: '50%',
                  width: 40,
                  height: 40,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: '0 4px 12px rgba(255,213,79,0.4)',
                  animation: 'pulse 2s infinite'
                }}>
                  <Star sx={{ color: 'white', fontSize: 20 }} />
                </Box>
              </Box>
            </Box>
          )}
          
          <Typography variant="h4" fontWeight={800} sx={{ 
            fontSize: { xs: '1.8rem', sm: '2.2rem' },
            textAlign: 'center',
            textShadow: '0 2px 4px rgba(0,0,0,0.3)',
            mb: 1
          }}>
            🌟 {isSequence ? 'Reading Adventure' : 'Reading Challenge'} 🌟
          </Typography>
          
          {studentName && (
            <Typography variant="h6" fontWeight={600} sx={{ 
              textAlign: 'center', color: '#ffd54f',
              fontSize: { xs: '1.1rem', sm: '1.25rem' }
            }}>
              Hey there, {studentName}! 👋
            </Typography>
          )}
          
          <Typography variant="h5" fontWeight={700} sx={{ 
            mt: 2, textAlign: 'center',
            fontSize: { xs: '1.3rem', sm: '1.5rem' },
            color: '#e1f5fe'
          }}>
            📚 {title}
          </Typography>
          
          {isSequence && (
            <Box sx={{ 
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              mt: 1, gap: 1
            }}>
              <PlayCircle sx={{ color: '#ffd54f' }} />
              <Typography variant="body1" sx={{ color: '#ffd54f', fontWeight: 600 }}>
                Story {currentPassageIndex + 1} of {sequence?.passages?.length || 1}
              </Typography>
            </Box>
          )}
        </Box>

        <Box sx={{ p: { xs: 1.5, sm: 2.5 } }}>
          {/* Microphone Check Section - Moved to top for better visibility */}
          <Box sx={{ 
            background: 'linear-gradient(135deg, #fff3e0 0%, #e8f5e8 100%)',
            borderRadius: 3, p: { xs: 1.5, sm: 2 }, mb: 2,
            border: '2px solid #ffcc02',
            boxShadow: '0 8px 25px rgba(255,204,2,0.2)'
          }}>
            <Typography variant="h6" fontWeight={700} sx={{ 
              color: '#667eea', mb: 1.5, textAlign: 'center',
              fontSize: { xs: '1rem', sm: '1.2rem' }
            }}>
              🎤 First, Let's Check Your Microphone! 🎤
            </Typography>
            
            <Box sx={{ 
              display: 'flex', flexDirection: { xs: 'column', sm: 'row' },
              alignItems: 'center', gap: 1.5, mb: 1.5, justifyContent: 'center'
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {micStatus === 'granted' ? (
                  <CheckCircle sx={{ color: '#4caf50', fontSize: 24 }} />
                ) : (
                  <MicNone sx={{ color: micStatus === 'denied' ? '#f44336' : '#ff9800', fontSize: 24 }} />
                )}
                <Chip
                  label={
                    micStatus === 'granted' 
                      ? '✅ Microphone Ready!' 
                      : micStatus === 'denied' 
                      ? '❌ Microphone Blocked' 
                      : '⚠️ Need Microphone Access'
                  }
                  color={micStatus === 'granted' ? 'success' : micStatus === 'denied' ? 'error' : 'warning'}
                  sx={{ fontWeight: 600, fontSize: { xs: '0.8rem', sm: '0.9rem' } }}
                />
              </Box>
              
              <Button
                variant="contained"
                onClick={handleCheckMic}
                disabled={checkingMic}
                sx={{ 
                  fontWeight: 700,
                  borderRadius: 3,
                  background: 'linear-gradient(135deg, #4fc3f7 0%, #29b6f6 100%)',
                  fontSize: { xs: '0.9rem', sm: '1rem' },
                  px: { xs: 2, sm: 3 }
                }}
              >
                {checkingMic ? '🔍 Checking…' : micStatus === 'granted' ? '🔄 Test Again' : '🎤 Setup Microphone'}
              </Button>
            </Box>

            {micStatus === 'denied' && (
              <Alert severity="warning" sx={{ 
                mb: 1, borderRadius: 2,
                '& .MuiAlert-message': { fontSize: { xs: '0.9rem', sm: '1rem' } }
              }}>
                🚫 Microphone access is blocked. Please enable it in your browser settings, then click "Test Again".
              </Alert>
            )}
          </Box>

          {/* Start Button Section - Moved right after microphone check */}
          <Box sx={{ 
            display: 'flex', 
            flexDirection: { xs: 'column', sm: 'row' },
            gap: { xs: 2, sm: 3 }, 
            justifyContent: 'center', 
            alignItems: 'center',
            mb: 3
          }}>
            <Button
              variant="contained"
              onClick={handleStart}
              disabled={micStatus !== 'granted'}
              sx={{ 
                fontSize: { xs: '1.4rem', sm: '1.6rem' }, 
                py: { xs: 2, sm: 2.5 }, 
                px: { xs: 4, sm: 6 }, 
                borderRadius: { xs: 3, sm: 4 },
                fontWeight: 800, 
                background: micStatus === 'granted' 
                  ? 'linear-gradient(135deg, #4caf50 0%, #66bb6a 50%, #8bc34a 100%)' 
                  : 'linear-gradient(135deg, #ccc 0%, #999 100%)',
                color: 'white',
                textTransform: 'none',
                boxShadow: micStatus === 'granted' 
                  ? '0 12px 30px rgba(76,175,80,0.4)' 
                  : '0 4px 10px rgba(0,0,0,0.2)',
                transition: 'all 0.3s ease',
                position: 'relative',
                overflow: 'hidden',
                minWidth: { xs: '280px', sm: 'auto' },
                animation: micStatus === 'granted' ? 'bounce 2s infinite' : 'none',
                '&:hover': {
                  transform: micStatus === 'granted' ? 'scale(1.05)' : 'none',
                  boxShadow: micStatus === 'granted' ? '0 15px 35px rgba(76,175,80,0.5)' : 'none'
                },
                '&:active': {
                  transform: micStatus === 'granted' ? 'scale(0.98)' : 'none'
                },
                '&:disabled': {
                  background: 'linear-gradient(135deg, #ccc 0%, #999 100%)',
                  color: '#666',
                  animation: 'none'
                },
                '&::before': micStatus === 'granted' ? {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: '-100%',
                  width: '100%',
                  height: '100%',
                  background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent)',
                  animation: 'shimmer 2s infinite',
                  pointerEvents: 'none'
                } : {}
              }}
            >
              🚀 {isSequence ? 'Start Adventure!' : 'Start Reading!'} 🚀
            </Button>
          </Box>

          {/* Instructions Section - Now collapsible and more compact */}
          <Box mb={2}>
            <Typography variant="h6" fontWeight={700} sx={{ 
              color: '#667eea', mb: 1.5, textAlign: 'center',
              fontSize: { xs: '1.1rem', sm: '1.3rem' }
            }}>
              📋 Quick Instructions
            </Typography>
            <Box sx={{ 
              background: 'linear-gradient(135deg, #e3f2fd 0%, #f3e5f5 50%, #fff3e0 100%)', 
              borderRadius: { xs: 2, sm: 3 }, 
              p: { xs: 2, sm: 2.5 },
              border: '2px solid #e1bee7',
              boxShadow: '0 8px 25px rgba(225,190,231,0.2)',
              position: 'relative',
              overflow: 'hidden',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: -2,
                left: -2,
                right: -2,
                bottom: -2,
                background: 'linear-gradient(45deg, #e1bee7, #b39ddb, #81c784, #ffcc02)',
                borderRadius: { xs: 2, sm: 3 },
                zIndex: -1
              }
            }}>
              {instructions.map((instruction, idx) => (
                <Box key={idx} sx={{ 
                  display: 'flex', alignItems: 'flex-start', mb: 1.5,
                  '&:last-child': { mb: 0 }
                }}>
                  <Box sx={{ 
                    minWidth: { xs: 24, sm: 32 }, height: { xs: 24, sm: 32 },
                    background: 'linear-gradient(135deg, #4fc3f7 0%, #29b6f6 100%)',
                    borderRadius: '50%', display: 'flex', alignItems: 'center',
                    justifyContent: 'center', mr: 2, mt: 0.5
                  }}>
                    <Typography sx={{ 
                      color: 'white', fontWeight: 'bold',
                      fontSize: { xs: '0.8rem', sm: '1rem' }
                    }}>
                      {idx + 1}
                    </Typography>
                  </Box>
                  <Typography sx={{ 
                    color: '#444', 
                    fontSize: { xs: '0.9rem', sm: '1rem' },
                    lineHeight: 1.4, flex: 1
                  }}>
                    {instruction}
                  </Typography>
                </Box>
              ))}
              {sequenceNotes.map((note, idx) => (
                <Box key={`s-${idx}`} sx={{ 
                  display: 'flex', alignItems: 'flex-start', mb: 1.5,
                  '&:last-child': { mb: 0 }
                }}>
                  <Box sx={{ 
                    minWidth: { xs: 24, sm: 32 }, height: { xs: 24, sm: 32 },
                    background: 'linear-gradient(135deg, #ff9800 0%, #f57c00 100%)',
                    borderRadius: '50%', display: 'flex', alignItems: 'center',
                    justifyContent: 'center', mr: 2, mt: 0.5
                  }}>
                    <Star sx={{ 
                      color: 'white', 
                      fontSize: { xs: 14, sm: 18 }
                    }} />
                  </Box>
                  <Typography sx={{ 
                    color: '#444', 
                    fontSize: { xs: '0.9rem', sm: '1rem' },
                    lineHeight: 1.4, flex: 1
                  }}>
                    {note}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>

          {/* Sequence Cards Section - Moved to bottom and more compact */}
          {isSequence && sequence?.passages?.length > 0 && (
            <Box mb={2}>
              <Typography variant="h6" fontWeight={700} sx={{ 
                color: '#667eea', mb: 1.5, textAlign: 'center',
                fontSize: { xs: '1.1rem', sm: '1.3rem' }
              }}>
                📚 Your Reading Stories
              </Typography>
              <Box sx={{ 
                display: 'grid', 
                gridTemplateColumns: { xs: '1fr', sm: 'repeat(auto-fit, minmax(200px, 1fr))' },
                gap: { xs: 1.5, sm: 2 }
              }}>
                {sequence.passages.map((p, idx) => (
                  <Card key={p.passageId?._id || idx} sx={{
                    border: idx === currentPassageIndex ? '3px solid #4fc3f7' : '2px solid #e0e0e0',
                    borderRadius: 2,
                    transition: 'all 0.3s ease',
                    transform: idx === currentPassageIndex ? 'scale(1.03)' : 'scale(1)',
                    boxShadow: idx === currentPassageIndex 
                      ? '0 8px 20px rgba(79,195,247,0.3)' 
                      : '0 4px 12px rgba(0,0,0,0.1)',
                    background: idx === currentPassageIndex 
                      ? 'linear-gradient(135deg, #e3f2fd 0%, #e1f5fe 100%)' 
                      : 'linear-gradient(135deg, #fafafa 0%, #f5f5f5 100%)',
                    position: 'relative',
                    overflow: 'hidden'
                  }}>
                    {idx === currentPassageIndex && (
                      <Box sx={{
                        position: 'absolute',
                        top: 6,
                        right: 6,
                        background: 'linear-gradient(135deg, #ff6b6b 0%, #ffa726 100%)',
                        borderRadius: '50%',
                        width: 28,
                        height: 28,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 2,
                        animation: 'pulse 2s infinite'
                      }}>
                        <Star sx={{ color: 'white', fontSize: 16 }} />
                      </Box>
                    )}
                    
                    <Box sx={{ p: { xs: 1.5, sm: 2 }, textAlign: 'center', position: 'relative' }}>
                      {p.passageId?.image ? (
                        <Box sx={{ 
                          position: 'relative',
                          mb: 1.5,
                          borderRadius: 2,
                          overflow: 'hidden',
                          boxShadow: '0 4px 15px rgba(0,0,0,0.12)'
                        }}>
                          <CardMedia
                            component="img"
                            height="60"
                            image={`${process.env.REACT_APP_API_URL}/passages/${p.passageId.image}`}
                            alt={p.passageId?.title || 'Story'}
                            sx={{ 
                              borderRadius: 2, 
                              objectFit: 'cover',
                              width: '100%'
                            }}
                          />
                        </Box>
                      ) : (
                        <Box sx={{
                          height: 60,
                          borderRadius: 2,
                          mb: 1.5,
                          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          position: 'relative'
                        }}>
                          <Typography variant="h5" sx={{ 
                            color: 'white', 
                            fontWeight: 800
                          }}>
                            📚
                          </Typography>
                        </Box>
                      )}
                      
                      <Typography variant="body1" fontWeight={700} sx={{ 
                        color: idx === currentPassageIndex ? '#1976d2' : '#444',
                        fontSize: { xs: '0.9rem', sm: '1rem' },
                        mb: 1,
                        lineHeight: 1.3
                      }}>
                        {idx === currentPassageIndex && '✨ '} 
                        {p.passageId?.title || `Story ${idx + 1}`}
                        {idx === currentPassageIndex && ' ✨'}
                      </Typography>
                      
                      {idx === currentPassageIndex ? (
                        <Box sx={{ 
                          background: 'linear-gradient(135deg, #4fc3f7 0%, #29b6f6 100%)',
                          color: 'white',
                          borderRadius: 1.5,
                          py: 0.5,
                          px: 1.5,
                          fontWeight: 700,
                          fontSize: { xs: '0.75rem', sm: '0.85rem' },
                          textTransform: 'uppercase',
                          letterSpacing: 0.5
                        }}>
                          🎯 Current
                        </Box>
                      ) : idx < currentPassageIndex ? (
                        <Box sx={{ 
                          background: 'linear-gradient(135deg, #4caf50 0%, #8bc34a 100%)',
                          color: 'white',
                          borderRadius: 1.5,
                          py: 0.5,
                          px: 1.5,
                          fontWeight: 700,
                          fontSize: { xs: '0.75rem', sm: '0.85rem' },
                          textTransform: 'uppercase',
                          letterSpacing: 0.5
                        }}>
                          ✅ Done
                        </Box>
                      ) : (
                        <Box sx={{ 
                          background: 'linear-gradient(135deg, #e0e0e0 0%, #bdbdbd 100%)',
                          color: '#666',
                          borderRadius: 1.5,
                          py: 0.5,
                          px: 1.5,
                          fontWeight: 600,
                          fontSize: { xs: '0.75rem', sm: '0.85rem' },
                          textTransform: 'uppercase',
                          letterSpacing: 0.5
                        }}>
                          ⏳ Next
                        </Box>
                      )}
                    </Box>
                  </Card>
                ))}
              </Box>
            </Box>
          )}
        </Box>
      </Paper>
    </Box>
  );
}
